import { type InfraScheduleItem, type InfraService } from '@carsfinders/contracts';
import { Plus, Server } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { t } from '../../lib/i18n';
import { kyivToday } from '../analytics/format';
import { financeErrorMessage, isAccessError } from '../finance/api';
import { PanelError, PanelLoading } from '../finance/components/panel-state';
import {
  useArchiveInfraService,
  useInfraPayments,
  useInfraServices,
  useInfraSummary,
  useSeedInfraDefaults,
} from './api';
import { InfraKpiTiles, InfraKpiTilesSkeleton } from './components/infra-kpi-tiles';
import { InfraPayDialog, type PayTarget } from './components/infra-pay-dialog';
import { InfraPaymentsTable } from './components/infra-payments-table';
import { InfraScheduleChart } from './components/infra-schedule-chart';
import { InfraServiceDrawer } from './components/infra-service-drawer';
import { InfraServicesTable } from './components/infra-services-table';
import { InfraUpcomingList } from './components/infra-upcoming-list';
import { historyRange } from './format';

type DrawerState = { mode: 'create' } | { mode: 'edit'; service: InfraService } | null;

/**
 * «Інфраструктура» (`/infra-costs`, spec §6) — what the platform pays its
 * providers: KPI in USD, the 12-month payment chart, the upcoming and overdue
 * payments with «Оплачено», the service registry with its form and archive,
 * and the payment history. Superadmin only (route + nav + api).
 */
export function InfraCostsPage() {
  const [showArchived, setShowArchived] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [pay, setPay] = useState<PayTarget | null>(null);
  const [archiving, setArchiving] = useState<InfraService | null>(null);

  const today = kyivToday();
  const summary = useInfraSummary();
  const services = useInfraServices(showArchived);
  const payments = useInfraPayments(historyRange(today));
  const seed = useSeedInfraDefaults();
  const archive = useArchiveInfraService();

  if ([summary.error, services.error, payments.error].some(isAccessError)) return <AccessDenied />;

  const isEmpty = services.data !== undefined && services.data.length === 0 && !showArchived;

  const openPay = (item: InfraScheduleItem) => {
    const service = services.data?.find((s) => s.id === item.serviceId);
    setPay({
      service: service ?? {
        id: item.serviceId,
        name: item.serviceName,
        amount: item.amount,
        currency: item.currency,
      },
      dueDate: item.dueDate,
      amount: item.amount,
      currency: item.currency,
    });
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('admin.infraCosts.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            {t('admin.infraCosts.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDrawer({ mode: 'create' })}
          className="inline-flex h-10 items-center gap-1.5 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
        >
          <Plus size={16} aria-hidden="true" />
          {t('admin.infraCosts.addService')}
        </button>
      </div>

      {services.isLoading ? (
        <PanelLoading />
      ) : services.isError ? (
        <PanelError error={services.error} fallbackKey="admin.infraCosts.failed" />
      ) : isEmpty ? (
        <section
          data-testid="infra-empty"
          className="flex flex-col items-center gap-3 rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center"
        >
          <Server size={28} className="text-cf-ink-subtle" aria-hidden="true" />
          <h2 className="text-[17px] font-semibold">{t('admin.infraCosts.empty.title')}</h2>
          <p className="max-w-md text-[13px] leading-[1.5] text-cf-ink-muted">{t('admin.infraCosts.empty.body')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              data-testid="infra-seed"
              disabled={seed.isPending}
              onClick={() => seed.mutate()}
              className="h-10 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:opacity-40"
            >
              {seed.isPending ? t('admin.infraCosts.empty.seeding') : t('admin.infraCosts.empty.seed')}
            </button>
            <button
              type="button"
              onClick={() => setShowArchived(true)}
              className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[14px] font-semibold text-cf-ink hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
            >
              {t('admin.infraCosts.services.showArchived')}
            </button>
          </div>
          <div aria-live="polite" className="text-[13px]">
            {seed.isError && (
              <p className="text-cf-danger">{financeErrorMessage(seed.error) ?? t('admin.infraCosts.empty.seedFailed')}</p>
            )}
          </div>
        </section>
      ) : (
        <>
          {summary.isLoading ? (
            <InfraKpiTilesSkeleton />
          ) : summary.isError ? (
            <PanelError error={summary.error} fallbackKey="admin.infraCosts.failed" />
          ) : summary.data ? (
            <InfraKpiTiles summary={summary.data} />
          ) : null}

          <Card title={t('admin.infraCosts.chart.title')}>
            {summary.isLoading ? (
              <div className="h-[168px] animate-pulse rounded-cf-md bg-cf-fill" aria-busy="true" />
            ) : summary.data ? (
              <InfraScheduleChart months={summary.data.months} />
            ) : null}
          </Card>

          <Card title={t('admin.infraCosts.upcoming.title')}>
            {summary.isLoading ? (
              <PanelLoading />
            ) : summary.data ? (
              <InfraUpcomingList items={summary.data.upcoming} onPay={openPay} />
            ) : null}
          </Card>

          <Card title={t('admin.infraCosts.services.title')}>
            <InfraServicesTable
              services={services.data ?? []}
              showArchived={showArchived}
              onToggleArchived={setShowArchived}
              onEdit={(service) => setDrawer({ mode: 'edit', service })}
              onArchive={setArchiving}
            />
          </Card>
        </>
      )}

      <Card title={t('admin.infraCosts.payments.title')}>
        {payments.isLoading ? (
          <PanelLoading />
        ) : payments.isError ? (
          <PanelError error={payments.error} fallbackKey="admin.infraCosts.failed" />
        ) : (
          <InfraPaymentsTable payments={payments.data ?? []} />
        )}
      </Card>

      {drawer && (
        <InfraServiceDrawer
          key={drawer.mode === 'edit' ? drawer.service.id : 'new'}
          service={drawer.mode === 'edit' ? drawer.service : undefined}
          onClose={() => setDrawer(null)}
        />
      )}

      {pay && <InfraPayDialog target={pay} onClose={() => setPay(null)} />}

      <ConfirmDialog
        open={archiving !== null}
        title={t('admin.infraCosts.archive.title')}
        message={
          <>
            {t('admin.infraCosts.archive.body', { service: archiving?.name || '—' })}
            {archive.isError && (
              <span role="alert" className="mt-2 block text-cf-danger">
                {financeErrorMessage(archive.error) ?? t('admin.infraCosts.archive.failed')}
              </span>
            )}
          </>
        }
        confirmLabel={t('admin.infraCosts.archive.confirm')}
        cancelLabel={t('admin.infraCosts.form.cancel')}
        busy={archive.isPending}
        onCancel={() => {
          archive.reset();
          setArchiving(null);
        }}
        onConfirm={() => {
          if (!archiving) return;
          archive.mutate(archiving.id, { onSuccess: () => setArchiving(null) });
        }}
      />
    </>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:px-[18px] lg:py-4">
      <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">{title}</h2>
      {children}
    </section>
  );
}
